import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import { cn } from "@/utils/cn";
import { formatDate, formatFcfa } from "@/utils/format";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import {
  PLATFORM_CONTRAT_STATUT_LABELS,
  PLATFORM_CONTRAT_STATUT_TONES,
  PLATFORM_SCOPE_LABELS,
  platformContratReference,
  platformLocataireNomComplet,
} from "@/store/platform/types";
import { BIEN_TYPE_LABELS, type BienType } from "@/pages/proprietaire/biens/types";
import PlatformContratForm from "@/pages/super-admin/components/PlatformContratForm";

export default function SuperAdminContratDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getContrat, getImmeuble, getBien, getLocataire, removeContrat } = usePlatformData();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const contrat = id ? getContrat(id) : undefined;

  if (!contrat) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/super-admin/contrats"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux contrats
        </Link>
        <EmptyState
          icon="ri-file-text-line"
          title="Contrat introuvable"
          description="Ce contrat n'existe pas ou n'est plus disponible."
          action={
            <Link
              to="/super-admin/contrats"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les contrats
            </Link>
          }
        />
      </div>
    );
  }

  const bien = getBien(contrat.bienId);
  const immeuble = getImmeuble(contrat.immeubleId);
  const locataire = getLocataire(contrat.locataireId);
  const reference = platformContratReference(contrat.id);

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Référence", value: reference, icon: "ri-price-tag-3-line" },
    { label: "Espace", value: PLATFORM_SCOPE_LABELS[contrat.scope], icon: "ri-layout-grid-line" },
    {
      label: "Bien",
      value: bien ? `${BIEN_TYPE_LABELS[bien.type as BienType]}${bien.reference ? ` · ${bien.reference}` : ""}` : "—",
      icon: "ri-home-4-line",
    },
    { label: "Immeuble", value: immeuble?.nom ?? "—", icon: "ri-building-2-line" },
    {
      label: "Locataire",
      value: locataire ? platformLocataireNomComplet(locataire) : "—",
      icon: "ri-user-3-line",
    },
    { label: "Date de début", value: formatDate(contrat.dateDebut), icon: "ri-calendar-check-line" },
    { label: "Date de fin", value: contrat.dateFin ? formatDate(contrat.dateFin) : "—", icon: "ri-calendar-line" },
    { label: "Loyer mensuel", value: formatFcfa(contrat.loyerMensuel), icon: "ri-wallet-3-line" },
    { label: "Caution", value: contrat.caution > 0 ? formatFcfa(contrat.caution) : "—", icon: "ri-shield-check-line" },
    { label: "Avance", value: contrat.avance > 0 ? formatFcfa(contrat.avance) : "—", icon: "ri-money-cny-circle-line" },
    {
      label: "Jour d'échéance",
      value: contrat.jourEcheance > 0 ? `Le ${contrat.jourEcheance} du mois` : "—",
      icon: "ri-calendar-event-line",
    },
    { label: "Statut", value: PLATFORM_CONTRAT_STATUT_LABELS[contrat.statut], icon: "ri-checkbox-circle-line" },
    { label: "Créé le", value: formatDate(contrat.createdAt), icon: "ri-calendar-line" },
  ];

  const liens: { label: string; to: string; icon: string; value: string }[] = [
    {
      label: "Immeuble concerné",
      to: immeuble ? `/super-admin/immeubles/${immeuble.id}` : "/super-admin/immeubles",
      icon: "ri-building-2-line",
      value: immeuble?.nom ?? "Non renseigné",
    },
    {
      label: "Bien concerné",
      to: bien ? `/super-admin/biens/${bien.id}` : "/super-admin/biens",
      icon: "ri-home-4-line",
      value: bien ? `${BIEN_TYPE_LABELS[bien.type as BienType]}${bien.reference ? ` · ${bien.reference}` : ""}` : "Non renseigné",
    },
    {
      label: "Locataire concerné",
      to: locataire ? `/super-admin/locataires/${locataire.id}` : "/super-admin/locataires",
      icon: "ri-user-3-line",
      value: locataire ? platformLocataireNomComplet(locataire) : "Non renseigné",
    },
  ];

  const confirmDelete = () => {
    removeContrat(contrat.id);
    setDeleteOpen(false);
    navigate("/super-admin/contrats");
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/super-admin/contrats"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux contrats
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
            <i className="ri-file-text-line text-2xl" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={PLATFORM_CONTRAT_STATUT_TONES[contrat.statut]}>
                {PLATFORM_CONTRAT_STATUT_LABELS[contrat.statut]}
              </Badge>
              <Badge tone={contrat.scope === "agence" ? "accent" : "primary"}>
                {PLATFORM_SCOPE_LABELS[contrat.scope]}
              </Badge>
              <Badge tone="neutral" icon="ri-price-tag-3-line">
                {reference}
              </Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{reference}</h2>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-user-3-line text-base text-foreground-400" aria-hidden="true" />
              <span className="truncate">
                {locataire ? platformLocataireNomComplet(locataire) : "Locataire non renseigné"}
              </span>
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              Du {formatDate(contrat.dateDebut)}
              {contrat.dateFin ? ` au ${formatDate(contrat.dateFin)}` : ""}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-edit-line text-base" aria-hidden="true" />
            Modifier
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations du contrat
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={cn(info.icon, "text-base")} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                Éléments liés
              </p>
              <div className="mt-2 flex flex-col gap-2">
                {liens.map((lien) => (
                  <Link
                    key={lien.label}
                    to={lien.to}
                    className="link-arrow flex items-center gap-3 rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2.5 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                      <i className={cn(lien.icon, "text-base")} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] text-foreground-500">{lien.label}</span>
                      <span className="block truncate text-sm font-label font-medium text-foreground-950">
                        {lien.value}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>

            {contrat.document && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Document du contrat
                </p>
                <p className="mt-2 flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-700">
                  <i className="ri-file-paper-2-line text-base text-foreground-400" aria-hidden="true" />
                  <span className="truncate">{contrat.document}</span>
                </p>
              </div>
            )}

            {contrat.conditionsParticulieres && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Conditions particulières
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{contrat.conditionsParticulieres}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex h-fit flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-links-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Accès rapides</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {[
              { label: "Immeubles", to: "/super-admin/immeubles", icon: "ri-building-2-line", hint: "Tous les immeubles" },
              { label: "Biens", to: "/super-admin/biens", icon: "ri-home-4-line", hint: "Tous les biens" },
              { label: "Locataires", to: "/super-admin/locataires", icon: "ri-user-3-line", hint: "Tous les locataires" },
              { label: "Contrats", to: "/super-admin/contrats", icon: "ri-file-text-line", hint: "Tous les contrats" },
            ].map((module) => (
              <Link
                key={module.to}
                to={module.to}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={cn(module.icon, "text-base")} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-label font-medium text-foreground-950">
                    {module.label}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">{module.hint}</span>
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <PlatformContratForm open={editOpen} onClose={() => setEditOpen(false)} contrat={contrat} />

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        description={`Supprimer le contrat « ${reference} » ? Cette action est définitive et le retirera des espaces Propriétaire et Agence.`}
      />
    </div>
  );
}